import React, { useEffect, useState, Fragment, useRef, useMemo } from "react";
import { Grid, Dropdown, Menu, notification } from "antd";
import { useTranslation, Trans } from "react-i18next";
import { useForm, Controller } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";

import { Input, Button, Box, Pagination, Select, Modal } from "components";
import { DownloadIcon, EditIcon, MoreIcon } from "icons";
import { IOption } from "components/select/Select";
import {
  localStorageHelper,
  generateDynamicRecords,
  generateCSV,
  convertFilename,
  isCompanyAccount,
} from "utils";

import {
  GetDepartmentListActionCreators,
  GetDataGroupClassCreators,
  GetUserListBusinessActionCreators,
  UpdateUserStatusActionCreators,
  DeleteBusinessUserActionCreators,
  UpdateUserDepartmentActionCreators,
  UpdateReleaseDateActionCreators,
  DownloadBusinessUserCSVActionCreators,
  ContractInformationActionCreators,
} from "redux/rootActions";
import {
  selectIsLoadingDepartmentsList,
  departmentsListResponse,
} from "redux/userListBusiness/departmentsList/departmentsListStates";
import { IReleaseDateItem } from "models/userListBusiness/updateReleaseDate";
import {
  UserListBusinessPayload,
  UserListBusinessResultData,
} from "models/userListBusiness/usersListBusiness";
import {
  selectIsDownloadingBusinessUserCSV,
  downloadBusinessUserCSVResponse,
} from "redux/userListBusiness/downloadBusinessUserCSV/downloadBusinessUserCSVStates";
import { dataGroupClassResponse } from "redux/bodyTemperature/groupClass/groupClassStates";
import {
  selectIsLoadingUserListBusiness,
  selectUserListBusinessErrorMessage,
  userListBusinessResponse,
} from "redux/userListBusiness/usersListBusiness/usersListBusinessStates";
import {
  selectIsUpdatingUserStatus,
  updateUserStatusErrorMessage,
  updateUserStatusResponse,
} from "redux/userListBusiness/updateUserStatus/updateUserStatusStates";
import {
  selectIsDeletingBusinessUser,
  deleteBusinessUserErrorMessage,
  deleteBusinessUserResponse,
} from "redux/userListBusiness/deleteBusinessUser/deleteBusinessUserStates";
import {
  selectIsUpdatingUserDepartment,
  updateUserDepartmentErrorMessage,
  updateUserDepartmentResponse,
} from "redux/userListBusiness/updateUserDepartment/updateUserDepartmentStates";
import {
  selectIsUpdatingReleaseDate,
  updateReleaseDateErrorMessage,
  updateReleaseDateResponse,
} from "redux/userListBusiness/updateReleaseDate/updateReleaseDateStates";
import { contractInformationDataResponse } from "redux/contract/information/contractInformationStates";

import {
  TaskWrapperBusiness,
  TaskWrapperRight,
  PaginationWrapper,
  ContentWrapper,
  Text,
  SearchWrapper,
  WrapperBtnForm,
  BusinessTableStyled,
  SelectWrapper,
  ApplicationStatusLabel,
  DeleteModalStyled,
  WrapperBtnFooter,
  TextModalDelete,
  EditModalStyled,
  SearchWrapperBusinessLeft,
  WrapperSelectAndBtn2,
} from "./UserList.style";
import {
  ItemContainer,
  CheckboxStyled4,
  WrapperItem,
} from "screens/bodyTemperature/BodyTemperature.style";
import {
  ErrorMessage,
  WrapperErrorMessage,
} from "screens/contractInformation/ContractInformation.style";

type FormStatus = "updating" | "";

const { useBreakpoint } = Grid;

const UserListBusiness: React.FC = () => {
  const { t }: any = useTranslation("translation", { keyPrefix: "user-list" });
  const { t: t1 }: any = useTranslation("translation", { keyPrefix: "layout" });
  const { t: t2 }: any = useTranslation("translation", {
    keyPrefix: "body-temperature",
  });
  const { t: t3 }: any = useTranslation("translation", {
    keyPrefix: "common",
  });
  const { t: t4 }: any = useTranslation("translation", {
    keyPrefix: "class-and-activity-management",
  });
  const { t: t5 }: any = useTranslation("translation", {
    keyPrefix: "contract",
  });
  const { t: t6 }: any = useTranslation("translation", {
    keyPrefix: "message-delivery",
  });

  const screens = useBreakpoint();
  const dispatch = useDispatch();
  const { watch, handleSubmit, control, setValue } = useForm();
  const currentLanguage = localStorageHelper.getItem("i18nextLng");
  const isLoadingDepartmentList = useSelector(selectIsLoadingDepartmentsList);
  const isLoadingUserListBusiness = useSelector(
    selectIsLoadingUserListBusiness
  );
  const isUpdatingUserStatus = useSelector(selectIsUpdatingUserStatus);
  const isDeletingBusinessUser = useSelector(selectIsDeletingBusinessUser);
  const isUpdatingUserDepartment = useSelector(selectIsUpdatingUserDepartment);
  const isUpdatingReleaseDate = useSelector(selectIsUpdatingReleaseDate);
  const isDownloadingBusinessUserCSV = useSelector(
    selectIsDownloadingBusinessUserCSV
  );

  const errorMessageUserList = useSelector(selectUserListBusinessErrorMessage);
  const errorUpdateUserStatus = useSelector(updateUserStatusErrorMessage);
  const errorDeleteBusinessUser = useSelector(deleteBusinessUserErrorMessage);
  const errorUpdateUserDepartment = useSelector(
    updateUserDepartmentErrorMessage
  );
  const errorUpdateReleaseDate = useSelector(updateReleaseDateErrorMessage);

  const departmentsListData = useSelector(departmentsListResponse);
  const csvDownloadSuccess = useSelector(downloadBusinessUserCSVResponse);
  const dataGroupClass = useSelector(dataGroupClassResponse);
  const userListBusinessData = useSelector(userListBusinessResponse);
  const updateUserStatusData = useSelector(updateUserStatusResponse);
  const deleteBusinessUserData = useSelector(deleteBusinessUserResponse);
  const updateUserDepartmentData = useSelector(updateUserDepartmentResponse);
  const updateReleaseDateData = useSelector(updateReleaseDateResponse);
  const contractInformationData = useSelector(contractInformationDataResponse);

  const scrollAbleRef = useRef<any>(undefined);
  const [visible, setVisible] = useState(false);
  const [formStatus, setFormStatus] = useState<FormStatus>("");
  const [selectedItem, setSelectedItem] =
    useState<UserListBusinessResultData>();
  const [recordOptions, setRecordOptions] = useState<IOption[]>([]);
  const [departmentsOptions, setDepartmentsOptions] = useState<IOption[]>([]);
  const [fileType, setFileType] = useState<any>();
  const [input, setInput] = useState<UserListBusinessPayload>({
    page: 1,
    per_page: 50,
    locale: currentLanguage || "ja",
  });
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [isVisibleDelete, setIsVisibleDelete] = useState<boolean>(false);
  const [treeData, setTreeData] = useState<any[]>([]);
  const [departmentOptions, setDepartmentOptions] = useState<any[]>([]);
  const [applicationStatusFilter, setApplicationStatusFilter] =
    useState<number>(0);
  const [editRemainingDays, setEditRemainingDays] = useState<boolean>(false);
  const [selectedDepartmentId, setSelectedDepartmentId] =
    useState<any>(undefined);
  const [initialInputValues, setInitialInputValues] = useState<any[]>([]);
  const [inputValues, setInputValues] = useState<IReleaseDateItem[]>([]);
  const [error, setError] = useState<any>({});

  const totalRecord = userListBusinessData?.total_record || 0;

  const applicationStatusOptions = [
    {
      id: 0,
      value: 0,
      name: currentLanguage === "vi" ? "Tất cả" : "全て",
    },
    {
      id: 1,
      value: 1,
      name: currentLanguage === "vi" ? "Chờ phê duyệt" : "承認待ち",
    },
    {
      id: 2,
      value: 2,
      name: currentLanguage === "vi" ? "Đã phê duyệt" : "承認済み",
    },
    {
      id: 3,
      value: 3,
      name: currentLanguage === "vi" ? "Từ chối" : "却下",
    },
    // {
    //   id: 4,
    //   value: 4,
    //   name: i18n.language === 'vi' ? "Hủy bỏ" : "解除",
    // },
  ];

  useEffect(() => {
    if (dataGroupClass && dataGroupClass?.result.length) {
      const tempDepartmentOptions: any[] = [];
      const clone = [...dataGroupClass?.result];
      setTreeData(clone);
      if (clone && clone.length) {
        clone.map((item: any) => {
          if (item && item.sub_departments && item.sub_departments.length) {
            item.sub_departments.map((dept: any) =>
              tempDepartmentOptions.push({
                id: dept.id,
                value: dept.id,
                name: dept.name,
              })
            );
          }
        });
      }
      setDepartmentOptions(tempDepartmentOptions);
    }
  }, [dataGroupClass]);

  useEffect(() => {
    if (
      userListBusinessData &&
      userListBusinessData.result &&
      !userListBusinessData.result.length
    ) {
      setEditRemainingDays(false);
      setInputValues([]);
      setInitialInputValues([]);
    }
    if (
      userListBusinessData &&
      userListBusinessData.result &&
      userListBusinessData.result.length
    ) {
      const tempInputValues: any[] = [];
      const initialInputs: any[] = [];
      userListBusinessData.result.map((user: UserListBusinessResultData) => {
        tempInputValues.push({
          id: user.id,
          day_left: user.day_left,
        });
        initialInputs.push(user.day_left);
      });
      setEditRemainingDays(false);
      setInputValues(tempInputValues);
      // setInitialInputValues(tempInputValues);
      setInitialInputValues(initialInputs);
    }
  }, [userListBusinessData]);

  useEffect(() => {
    if (
      departmentsListData &&
      departmentsListData?.result &&
      !isLoadingDepartmentList
    ) {
      const tempDepartmentsOptions: IOption[] = [];
      departmentsListData.result.map((item: { id: number; name: string }) =>
        tempDepartmentsOptions.push({
          id: item.id,
          value: item.id,
          name: item.name,
        })
      );
      setDepartmentsOptions([
        {
          id: 0,
          value: "",
          name: t6("all"),
        },
        ...tempDepartmentsOptions,
      ]);
    }
  }, [departmentsListData]);

  useEffect(() => {
    dispatch(ContractInformationActionCreators.contractInformationAction());
    dispatch(GetDepartmentListActionCreators.getDepartmentsListAction());
    dispatch(
      GetDataGroupClassCreators.getDataGroupClassAction({
        locale: currentLanguage,
      })
    );
  }, []);

  const handleGetUserList = () => {
    const params: UserListBusinessPayload = {
      ...input,
      status: applicationStatusFilter,
    };

    dispatch(
      GetUserListBusinessActionCreators.getUserListBusinessAction(params)
    );
  };

  const handleCancel = () => {
    setVisible(false);
    setSelectedItem(undefined);
    setSelectedDepartmentId(undefined);
    handleGetUserList();
  };

  useEffect(() => {
    handleGetUserList();
  }, [input.per_page, input.page, applicationStatusFilter]);

  useEffect(() => {
    if (
      (updateUserStatusData && !isUpdatingUserStatus) ||
      (deleteBusinessUserData && !isDeletingBusinessUser)
    ) {
      setSelectedItem(undefined);
      handleGetUserList();
    }
  }, [updateUserStatusData, deleteBusinessUserData]);

  useEffect(() => {
    if (updateReleaseDateData && !isUpdatingReleaseDate) {
      setEditRemainingDays(false);
      handleGetUserList();
    }
  }, [updateReleaseDateData]);

  useEffect(() => {
    if (updateUserDepartmentData && !isUpdatingUserDepartment) {
      handleCancel();
      handleGetUserList();
    }
  }, [updateUserDepartmentData]);

  const handleFileDownload = () => {
    // const params: IUserListDownloadData = {
    //   c_department_id: watchClassField ? watchClassField : "",
    //   user_name: watchSearchNameField || "",
    //   locale: currentLanguage,
    // };
    // dispatch(CSVDownloadActionCreators.csvDownloadUserAction(params));
    dispatch(
      DownloadBusinessUserCSVActionCreators.downloadBusinessUserCSVAction()
    );
  };

  const menu = (
    <Menu>
      <Menu.Item>
        <Button
          icon={
            <DownloadIcon
              width="14px"
              height="14px"
              fill="currentColor"
              style={{ position: "absolute", left: 4, top: 4 }}
            />
          }
          name={t("excel-output")}
          background="#2AC769"
          color="#FFFFFF"
          border="none"
          fontSize={12}
          lineHeight="17px"
          fontWeight={700}
          padding="3px 7px 3px 16px"
          onClick={() => {
            setFileType("csv");
            handleFileDownload();
          }}
          disabled={isDownloadingBusinessUserCSV}
          //   margin="0 8px 0"
        />
      </Menu.Item>
    </Menu>
  );

  const showModal = (formStatus: FormStatus) => {
    setFormStatus(formStatus);
    setVisible(true);
  };

  const handleChangeRemainDaysInput = (e: any, id: number) => {
    if (inputValues && inputValues.length) {
      const tempInputVals = [...inputValues];
      const index = inputValues.findIndex(
        (item: IReleaseDateItem) => item.id === id
      );
      if (Number(e.target.value) < 1 || Number(e.target.value) > 500) {
        setError((prevState: any) => ({
          ...prevState,
          [id]: "day-invalid",
        }));
      } else {
        const newError = { ...error };
        delete newError[id];
        setError(newError);
      }

      tempInputVals[index].day_left = parseFloat(e.target.value);
      setInputValues(tempInputVals);
    }
  };

  const returnDayLeft = (id: number) => {
    if (inputValues && inputValues.length) {
      const dayLeft = inputValues.find(
        (item: IReleaseDateItem) => item.id === id
      )?.day_left;
      if (!dayLeft || (dayLeft && dayLeft >= 0)) {
        return "#3B3B3B";
      } else {
        return "#FB4E4E";
      }
    }
    return "#3B3B3B";
  };

  const returnComparedInputs = () => {
    const tempArr: IReleaseDateItem[] = [];
    if (
      inputValues &&
      initialInputValues &&
      inputValues.length &&
      initialInputValues.length
    ) {
      initialInputValues.map((input: any, index: number) => {
        if (input !== inputValues[index].day_left) {
          tempArr.push(inputValues[index]);
        }
      });
    }
    return tempArr;
  };

  const handleUpdateRemainingDays = () => {
    dispatch(
      UpdateReleaseDateActionCreators.updateReleaseDateAction({
        company_users: returnComparedInputs(),
      })
    );
  };

  const columns = [
    {
      title: t("full-name"),
      dataIndex: ["last_name", "first_name"],
      key: "first_name",
      width: 175,
      fixed: window.innerWidth < 576 ? "" : "left",
      columnName: "full_name",
      show: true,
      render: (name: string, row: UserListBusinessResultData) => (
        <div style={{ display: "flex" }}>
          <div style={{ marginTop: 3 }}>
            <EditIcon
              onClick={() => {
                setSelectedItem(row);
                showModal("updating");
              }}
              fill="currentColor"
              style={{ cursor: "pointer" }}
              width="16px"
              height="16px"
            />
          </div>
          <div style={{ marginLeft: 17 }}>
            {row["last_name"] + " " + row["first_name"]}
          </div>
        </div>
      ),
    },
    {
      title: () => {
        return (
          <ApplicationStatusLabel>
            {currentLanguage === "vi" ? (
              <div className="label">
                Tình trạng
                <br /> đăng ký
              </div>
            ) : (
              <div className="label">
                {t("application")}
                <br /> {t("status")}
              </div>
            )}
            <Select
              value={applicationStatusFilter}
              options={applicationStatusOptions}
              width="100px"
              onChange={(e: any) => setApplicationStatusFilter(e)}
            />
          </ApplicationStatusLabel>
        );
      },
      dataIndex: "application_status",
      key: "application_status",
      width: currentLanguage === "vi" ? 230 : 180,
      columnName: "other_name_type",
      show: true,
      render: (text: string, row: any) => (
        <div style={{ display: "flex" }}>
          <Text>{text}</Text>
        </div>
      ),
    },
    {
      title: t("activities"),
      dataIndex: "designation",
      key: "designation",
      columnName: "designation",
      width: 148,
      show: true,
    },
    {
      title: () => {
        return (
          <ApplicationStatusLabel style={{ gap: 8 }}>
            <div className="label">
              {t("cancelation")}
              <br /> {t("remaining-days")}
            </div>
            <Button
              color="#ffffff"
              fontSize={16}
              fontWeight={500}
              lineHeight="23px"
              background={editRemainingDays ? "#2F8CAE" : "#2AC769"}
              padding="4px 8px 3px"
              name={editRemainingDays ? t4("keep") : t4("edit")}
              border="none"
              bdr="6px"
              onClick={() => {
                if (!editRemainingDays) {
                  setEditRemainingDays(true);
                } else {
                  if (!Object.keys(error).length) {
                    setEditRemainingDays(false);
                    if (
                      returnComparedInputs() &&
                      returnComparedInputs().length
                    ) {
                      handleUpdateRemainingDays();
                    }
                  }
                }
              }}
            />
          </ApplicationStatusLabel>
        );
      },
      dataIndex: "day_left",
      key: "day_left",
      width: 128,
      show: contractInformationData?.result?.auto_release,
      render: (dayLeft: any, record: UserListBusinessResultData) => {
        if (editRemainingDays) {
          if (!dayLeft) {
            return;
          }
          return (
            <div style={{ position: "relative" }}>
              <Input
                defaultValue={record.day_left}
                type="number"
                value={
                  inputValues
                    .find((item: IReleaseDateItem) => item.id === record.id)
                    ?.day_left.toString() || undefined
                }
                width={"100%"}
                height={30}
                onChange={(e: any) => handleChangeRemainDaysInput(e, record.id)}
                style={{
                  color: returnDayLeft(record.id),
                }}
              />
              {error[record.id] && (
                <WrapperErrorMessage>
                  <ErrorMessage>{t5(error[record.id])}</ErrorMessage>
                </WrapperErrorMessage>
              )}
            </div>
          );
        } else {
          return (
            <div
              style={{
                color: returnDayLeft(record.id),
              }}
            >
              {dayLeft}
            </div>
          );
        }
      },
    },
    {
      title: t5("comment"),
      dataIndex: "free_comment",
      key: "free_comment",
      width: 396,
      show: true,
      render: (text: string) => {
        // if (!text) {
        //   return <div>-</div>;
        // }
        return <div>{text}</div>;
      },
    },
    {
      title: "",
      dataIndex: "",
      key: "",
      show: true,
      //   width: 250,
      render: (text: any, record: UserListBusinessResultData) => {
        return (
          <div
            style={{
              display: "flex",
              gap: 8,
              alignItems: "center",
              justifyContent: "flex-end",
            }}
          >
            <Button
              color="#ffffff"
              fontSize={16}
              fontWeight={500}
              lineHeight="23px"
              background="#B7B7B7"
              padding="4px 8px 3px"
              name={t("reject")}
              border="none"
              bdr="6px"
              disabled={
                selectedItem?.id &&
                selectedItem.id === record.id &&
                isUpdatingUserStatus
                  ? true
                  : false
              }
              onClick={() => {
                setSelectedItem(record);
                setIsVisible(true);
              }}
            />
            <Button
              color="#ffffff"
              fontSize={16}
              fontWeight={500}
              lineHeight="23px"
              background="#FB4E4E"
              padding="4px 8px 3px"
              name={t4("delete")}
              border="none"
              bdr="6px"
              disabled={
                selectedItem?.id &&
                selectedItem.id === record.id &&
                isDeletingBusinessUser
                  ? true
                  : false
              }
              onClick={() => {
                setSelectedItem(record);
                setIsVisibleDelete(true);
              }}
            />
          </div>
        );
      },
    },
    //   ].filter((item: any) => item.show);
  ].filter((item: any) => item.show);

  const handleCheckedItem = (item: any, itemType: string, e: any) => {
    item.checked = e;
    if (item[itemType].length) {
      item[itemType].map((newItem: any) => {
        handleCheckedItem(newItem, itemType, item.checked);
      });
    }
  };

  const onChangeCheckbox = (item: any, itemType: string) => (e: any) => {
    handleCheckedItem(item, itemType, e.target.checked);
    if (itemType === "sub_departments") {
      setTreeData([...treeData]);
    } else {
      // setTreeData2([...treeData2]);
    }
  };

  const handleResetTreeSelect = () => {
    treeData.forEach((item: any) => {
      handleCheckedItem(item, "sub_departments", false);
    });
    setTreeData([...treeData]);
  };

  const CompItem = ({
    item,
    itemType,
    children,
  }: {
    item: any;
    itemType: string;
    children: any;
  }) => {
    const { name, checked } = item;

    const handleCheckParent = (item: any, checked: boolean) => {
      item.checked = checked;
      if (itemType === "sub_departments") {
        setTreeData([...treeData]);
      } else {
        // setTreeData2([...treeData2]);
      }
    };

    useEffect(() => {
      if (item[itemType].length > 0) {
        if (
          !item[itemType].some(
            (d: any) => d.checked === false || d.checked === undefined
          )
        ) {
          if (!item.checked) {
            handleCheckParent(item, true);
          }
        } else {
          if (item.checked) {
            handleCheckParent(item, false);
          }
        }
      }
    }, [item[itemType]]);

    return (
      <>
        <ItemContainer>
          <CheckboxStyled4
            checked={checked}
            onChange={onChangeCheckbox(item, itemType)}
          >
            <div style={{ marginLeft: 10 }}>{name}</div>
          </CheckboxStyled4>
          {/* <Tooltip placement="topLeft" title={name}>
            </Tooltip> */}
        </ItemContainer>
        {children}
      </>
    );
  };

  const Item = ({ item, itemType }: { item: any; itemType: string }) => {
    return (
      <CompItem item={item} itemType={itemType}>
        {item[itemType].length > 0 &&
          item[itemType].map((i: any) => (
            <div style={{ marginLeft: 32 }} key={i.id}>
              <CompItem item={i} itemType={itemType}>
                {i[itemType].map((a: any) => (
                  <div style={{ marginLeft: 32 }} key={a.id}>
                    <Item item={a} itemType={itemType} />
                  </div>
                ))}
              </CompItem>
            </div>
          ))}
      </CompItem>
    );
  };

  const handleGetCheckedItemValue = (
    arr: any[],
    checkedArr: number[],
    itemType: string
  ) => {
    arr.forEach((item) => {
      if (item.checked) {
        checkedArr.push(item.id);
      } else {
        if (item[itemType].length) {
          handleGetCheckedItemValue(item[itemType], checkedArr, itemType);
        }
      }
    });
  };

  const getIdParent = () => {
    const idParentList = treeData.map((item: any) => {
      return item.id;
    });

    const checkedArr: number[] = [];
    handleGetCheckedItemValue(treeData, checkedArr, "sub_departments");
    const arrID: any = [];

    checkedArr.map((item: any) => {
      if (idParentList.includes(item)) {
        arrID.push(true);
      } else {
        arrID.push(false);
      }
    });

    const check = arrID.some((item: any) => !item);
    return !check && checkedArr.length === idParentList.length;
  };

  const handleGetValue = (tree: any, arr: any) => {
    tree.map((item: any) => {
      if (item.checked) {
        arr.push(item.name);
      }
      if (item.sub_departments.length) {
        handleGetValue(item.sub_departments, arr);
      }
    });
  };

  const handleCheckTreeData = () => {
    const checkedArr: number[] = [];
    handleGetCheckedItemValue(treeData, checkedArr, "sub_departments");
    return checkedArr.length;
  };

  const handleValueOption = () => {
    const arr: any = [];
    handleGetValue(treeData, arr);
    if (getIdParent()) return t6("all");
    if (arr.length > 1) return t2("multiple-selection");
    return arr[0];
  };

  const onSubmit = (data: { [x: string]: any }) => {
    // setOldGrade(data.grade);
    // setOldName(data.name);

    // if (oldGrade !== data.grade || oldName !== data.name) {
    //   setInput((prevState: any) => ({ ...prevState, page: 1 }));
    //   return;
    // }

    const params: UserListBusinessPayload = {
      ...input,
      c_department_id: data.grade || "",
      search: data.name || "",
      status: applicationStatusFilter,
    };

    setInput(params);
    dispatch(
      GetUserListBusinessActionCreators.getUserListBusinessAction(params)
    );
  };

  useEffect(() => {
    if (errorMessageUserList) {
      notification.error({
        placement: "bottomRight",
        message: errorMessageUserList,
      });
      dispatch(GetUserListBusinessActionCreators.handleResetDataAction());
    }
  }, [errorMessageUserList]);

  useEffect(() => {
    if (errorUpdateUserStatus) {
      notification.error({
        placement: "bottomRight",
        message: errorUpdateUserStatus,
      });
      dispatch(UpdateUserStatusActionCreators.handleResetAction());
    }
  }, [errorUpdateUserStatus]);

  useEffect(() => {
    if (errorDeleteBusinessUser) {
      notification.error({
        placement: "bottomRight",
        message: errorDeleteBusinessUser,
      });
      dispatch(DeleteBusinessUserActionCreators.handleResetAction());
    }
  }, [errorDeleteBusinessUser]);

  useEffect(() => {
    if (errorUpdateReleaseDate) {
      notification.error({
        placement: "bottomRight",
        message: errorUpdateReleaseDate,
      });
      dispatch(UpdateReleaseDateActionCreators.handleResetAction());
      handleGetUserList();
    }
  }, [errorUpdateReleaseDate]);

  useEffect(() => {
    if (errorUpdateUserDepartment) {
      notification.error({
        placement: "bottomRight",
        message: errorUpdateUserDepartment,
      });
      dispatch(UpdateUserDepartmentActionCreators.handleResetAction());
    }
  }, [errorUpdateUserDepartment]);

  useEffect(() => {
    if (
      csvDownloadSuccess &&
      csvDownloadSuccess.result &&
      !isDownloadingBusinessUserCSV
    ) {
      if (fileType === "csv") {
        const filename = `${convertFilename("business-users", new Date())}.csv`;
        const tempHeaders: any[] = [];
        const headers = csvDownloadSuccess.result.headers;
        headers.map((header: any) => {
          if (header.label === "mail") {
            tempHeaders.push({
              label: "メール",
              key: header.key,
            });
          } else if (header.label === "phone_number") {
            tempHeaders.push({
              label: "携帯番号",
              key: header.key,
            });
          } else {
            tempHeaders.push(header);
          }
        });
        generateCSV(filename, tempHeaders, csvDownloadSuccess.result.data);
      }
      dispatch(DownloadBusinessUserCSVActionCreators.handleResetAction());
    }
  }, [csvDownloadSuccess]);

  useEffect(() => {
    if (userListBusinessData && Object.keys(userListBusinessData).length) {
      let recordOptionsClone: IOption[] = [];
      recordOptionsClone = generateDynamicRecords(
        userListBusinessData?.total_record,
        t
      );
      setRecordOptions([...recordOptionsClone]);
    }
  }, [userListBusinessData]);

  const handleChangePage = (page: number) => {
    setEditRemainingDays(false);
    setInput((prevState: any) => ({ ...prevState, page }));

    if (
      scrollAbleRef.current &&
      window.scrollY >= scrollAbleRef.current.offsetTop
    ) {
      window.scrollTo(0, 0);
    }
  };

  const handleOnChangeSizeChange = (limit: number) => {
    setInput((prevState: any) => ({ ...prevState, per_page: limit, page: 1 }));
  };

  const handleSubmitOK = () => {
    setVisible(false);
    handleGetUserList();
  };

  const handleOrderByKey = (arr: any[], order: number, name: string) => {
    const idxItem = arr.findIndex((item: any) => {
      return item.columnName === name.trim();
    });

    if (idxItem > -1) {
      if (idxItem != order) {
        const splicedItem = arr.splice(idxItem, 1);
        arr.splice(order, 0, splicedItem[0]);
      }
    }
  };

  const ActionSection = () => {
    return (
      <TaskWrapperBusiness>
        <TaskWrapperRight>
          <Button
            icon={
              <DownloadIcon
                width="14px"
                height="14px"
                fill="currentColor"
                style={{ position: "absolute", left: 4, top: 4 }}
              />
            }
            name={t("excel-output")}
            background="#2AC769"
            color="#FFFFFF"
            border="none"
            fontSize={12}
            lineHeight="17px"
            fontWeight={700}
            padding="3px 7px 3px 16px"
            onClick={() => {
              setFileType("csv");
              handleFileDownload();
            }}
            // margin="0 8px 0"
          />
          <Dropdown overlay={menu} placement="bottomRight" arrow>
            <MoreIcon fill="#B7B7B7" />
          </Dropdown>
        </TaskWrapperRight>
      </TaskWrapperBusiness>
    );
  };

  const FooterModal = (
    color: string,
    backgroundBtnLeft: string,
    backgroundBtnRight: string,
    padding: string,
    nameBtnLeft: string,
    nameBtnRight: string,
    onCancel?: () => void,
    onOk?: () => void
  ) => {
    const border = `1px solid ${backgroundBtnLeft}`;
    return (
      <WrapperBtnForm>
        <Button
          type="reset"
          color={color}
          fontSize={16}
          fontWeight={700}
          lineHeight="16px"
          background={backgroundBtnLeft}
          padding={padding}
          name={nameBtnLeft}
          border={border}
          onClick={onCancel}
        />
        <Button
          type="submit"
          color={color}
          fontSize={16}
          fontWeight={700}
          lineHeight="16px"
          background={backgroundBtnRight}
          padding={padding}
          name={nameBtnRight}
          border={border}
          onClick={onOk}
        />
      </WrapperBtnForm>
    );
  };

  const ShowPagination = () => {
    return totalRecord > input.per_page ? (
      <PaginationWrapper>
        <Pagination
          current={input.page}
          onChange={handleChangePage}
          pageSize={input.per_page}
          total={totalRecord}
          showSizeChanger={false}
          showLessItems={!screens.xl}
        />
      </PaginationWrapper>
    ) : (
      <Fragment></Fragment>
    );
  };

  const renderForm = () => {
    switch (formStatus) {
      case "updating":
        return (
          visible && (
            <EditModalStyled
              type="form"
              title={t("change-of-affiliation")}
              visible={
                visible && selectedItem && selectedItem.id ? true : false
              }
              onCancel={handleCancel}
              onOk={handleSubmit(onSubmit)}
            >
              <Input
                label={t("unique-number")}
                width={"100%"}
                height={31}
                marginForm="0px 0px 24px 0px"
                disabled={true}
                value={selectedItem?.kojin_number}
              />
              <Input
                label={t("surname")}
                width={"100%"}
                height={31}
                marginForm="0px 0px 24px 0px"
                disabled={true}
                value={selectedItem?.first_name}
              />
              <Input
                label={t("given-name")}
                width={"100%"}
                height={31}
                marginForm="0px 0px 24px 0px"
                disabled={true}
                value={selectedItem?.last_name}
              />
              {/* <Select
                label={t("activities")}
                width="100%"
                height="31px"
                marginForm="0px 0px 48px 0px"
              /> */}
              <Select
                defaultValue={
                  selectedItem?.c_department_id
                    ? selectedItem?.c_department_id
                    : undefined
                }
                label={t("activities")}
                value={selectedDepartmentId}
                options={departmentsOptions}
                onChange={(e: any) => setSelectedDepartmentId(e)}
                placeholder={
                  isCompanyAccount()
                    ? t("select-affiliation")
                    : t("select-grade-or-class")
                }
                width="100%"
                marginForm="0px 0px 48px 0px"
              />
              {/* <Select
                label={t("activities")}
                style={
                  handleCheckTreeData()
                    ? { color: "#2AC769", fontWeight: 500 }
                    : { color: "#3B3B3B" }
                }
                value={handleValueOption()}
                placeholder={
                  isCompanyAccount()
                    ? t("select-affiliation")
                    : t("select-grade-or-class")
                }
                width="100%"
                dropdownRender={() => (
                  <div
                    style={{
                      padding: "7px 10px 5px",
                      maxHeight: "300px",
                      overflowX: "auto",
                    }}
                  >
                    {treeData.map((item: any) => {
                      return (
                        <WrapperItem key={item.id}>
                          <Item item={item} itemType="sub_departments" />
                        </WrapperItem>
                      );
                    })}
                  </div>
                )}
                marginForm="0px 0px 48px 0px"
              /> */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Button
                  color="#ffffff"
                  fontSize={16}
                  fontWeight={700}
                  lineHeight="16px"
                  background="#E0E0E0"
                  padding="13px 24px 11px"
                  name={t("cancel")}
                  border="none"
                  bdr="6px"
                  onClick={handleCancel}
                  disabled={isUpdatingUserDepartment}
                />
                <Button
                  color="#ffffff"
                  fontSize={16}
                  fontWeight={700}
                  lineHeight="16px"
                  background="#2AC769"
                  padding="13px 48px 11px"
                  name={t4("keep")}
                  border="none"
                  bdr="6px"
                  onClick={() => {
                    if (
                      selectedItem &&
                      selectedItem.id &&
                      selectedDepartmentId
                    ) {
                      dispatch(
                        UpdateUserDepartmentActionCreators.updateUserDepartmentAction(
                          {
                            id: selectedItem.id,
                            c_department_id: selectedDepartmentId,
                          }
                        )
                      );
                    }
                  }}
                  disabled={isUpdatingUserDepartment}
                />
              </div>
            </EditModalStyled>
          )
        );
      default:
        return;
    }
  };

  return (
    <>
      {renderForm()}
      <Box
        title={t1("user-management-list")}
        subTitle={`${t("total-number-of-people")}
        ${totalRecord}${t2("people")}`}
        padding="24px 16px 32px"
        action={
          <SelectWrapper>
            <div
              style={{
                fontSize: 14,
                fontWeight: 500,
                marginRight: 5,
              }}
            >
              {t("record")}
            </div>
            <Select
              defaultValue={50}
              options={recordOptions}
              onChange={handleOnChangeSizeChange}
            />
          </SelectWrapper>
        }
      >
        <SearchWrapper>
          <form onSubmit={handleSubmit(onSubmit)}>
            <SearchWrapperBusinessLeft style={{ marginBottom: 16 }}>
              <Controller
                control={control}
                name="name"
                render={({ field: { onChange } }) => (
                  <Input
                    placeholder={t("search-for-user")}
                    height={31}
                    onChange={onChange}
                  />
                )}
              />

              <WrapperSelectAndBtn2>
                <Controller
                  control={control}
                  name="grade"
                  render={({ field: { onChange } }) => (
                    <Select
                      placeholder={
                        isCompanyAccount()
                          ? t("select-affiliation")
                          : t("select-grade-or-class")
                      }
                      options={departmentsOptions}
                      onChange={onChange}
                    />
                  )}
                />

                <Button
                  type="submit"
                  name={t("search")}
                  background="#2AC769"
                  color="#FFFFFF"
                  border="none"
                  fontSize={16}
                  fontWeight={700}
                  padding="1px 16px"
                />
              </WrapperSelectAndBtn2>
            </SearchWrapperBusinessLeft>
          </form>

          <ShowPagination />
        </SearchWrapper>

        <ContentWrapper
          style={{ marginBottom: totalRecord > input.per_page ? 24 : 0 }}
          ref={scrollAbleRef}
        >
          <BusinessTableStyled
            dataSource={userListBusinessData?.result || []}
            columns={columns}
            actionSection={<ActionSection />}
            scroll={!userListBusinessData?.result ? {} : { x: "max-content" }}
            loading={isLoadingUserListBusiness || isUpdatingReleaseDate}
            bd={contractInformationData?.result?.auto_release}
          />
          {!userListBusinessData?.result && (
            <div
              style={{
                fontSize: 16,
                lineHeight: "18px",
                color: "#666666",
                fontWeight: 400,
                width: "100%",
                padding: "24px 16px 0px",
              }}
            >
              {t("no-matching-users")}
            </div>
          )}
        </ContentWrapper>
        <ShowPagination />
        <DeleteModalStyled
          type="confirm"
          footer={
            <WrapperBtnFooter>
              <Button
                onClick={() => {
                  setIsVisible(false);
                  setSelectedItem(undefined);
                }}
                background="#E0E0E0"
                color="#fff"
                name={t("cancel")}
                border="none"
                bdr="6px"
                fontSize={16}
                fontWeight={700}
              />
              <Button
                onClick={() => {
                  setIsVisible(false);
                  if (selectedItem && selectedItem.id) {
                    dispatch(
                      UpdateUserStatusActionCreators.updateUserStatusAction({
                        id: selectedItem.id,
                        status: 3,
                      })
                    );
                  }
                }}
                background="#FB4E4E"
                color="#fff"
                name={t("yes")}
                border="none"
                bdr="6px"
                fontSize={16}
                fontWeight={700}
              />
            </WrapperBtnFooter>
          }
          visible={isVisible && selectedItem && selectedItem.id ? true : false}
          onCancel={() => {
            setIsVisible(false);
            setSelectedItem(undefined);
          }}
        >
          <TextModalDelete>
            <Trans i18nKey="user-list.reject-application">
              「
              {{
                userName: selectedItem
                  ? `${selectedItem.last_name} ${selectedItem.first_name}`
                  : "",
              }}
              」の申請を却下します。
              {/* {{ userName: selectedPatient.name }}さんの登録を解除します。 */}
            </Trans>
          </TextModalDelete>

          <TextModalDelete>{t("are-you-sure")}</TextModalDelete>
        </DeleteModalStyled>
        <DeleteModalStyled
          type="confirm"
          footer={
            <WrapperBtnFooter>
              <Button
                onClick={() => {
                  setIsVisibleDelete(false);
                  setSelectedItem(undefined);
                }}
                background="#E0E0E0"
                color="#fff"
                name={t("cancel")}
                border="none"
                bdr="6px"
                fontSize={16}
                fontWeight={700}
              />
              <Button
                onClick={() => {
                  setIsVisibleDelete(false);
                  if (selectedItem && selectedItem.id) {
                    dispatch(
                      UpdateUserStatusActionCreators.updateUserStatusAction({
                        id: selectedItem.id,
                        status: 4,
                      })
                    );
                  }
                }}
                background="#FB4E4E"
                color="#fff"
                name={t("yes")}
                border="none"
                bdr="6px"
                fontSize={16}
                fontWeight={700}
              />
            </WrapperBtnFooter>
          }
          visible={
            isVisibleDelete && selectedItem && selectedItem.id ? true : false
          }
          onCancel={() => {
            setIsVisibleDelete(false);
            setSelectedItem(undefined);
          }}
        >
          <TextModalDelete>
            <Trans i18nKey="user-list.delete-business-user">
              「
              {{
                userName: selectedItem
                  ? `${selectedItem.last_name} ${selectedItem.first_name}`
                  : "",
              }}
              」を削除します。
              {/* {{ userName: selectedPatient.name }}さんの登録を解除します。 */}
            </Trans>
          </TextModalDelete>

          <TextModalDelete>{t("are-you-sure")}</TextModalDelete>
        </DeleteModalStyled>
      </Box>
    </>
  );
};

export default UserListBusiness;
